import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { api } from '@/utils/api';

interface Recommendations {
  at_risk: { creator_id: number; name: string; status: string; reason: string }[];
  replacements: { user_id: number; name: string | null; niche: string; followers: string; platforms: string; reason: string }[];
  summary: string;
  source: 'ai' | 'basic';
}

/** The live campaign page's "AI Recommendations" card (GET campaigns/{id}/replacement-recommendations). */
export default function AiRecommendations({ campaignId }: { campaignId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Recommendations | null>(null);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const reply = await api<Recommendations>(`/campaigns/${campaignId}/replacement-recommendations`);
      // The API lists a creator once per platform, biggest first: keep each creator's top one.
      const seen = new Set<number>();
      reply.replacements = reply.replacements.filter((creator) => !seen.has(creator.user_id) && Boolean(seen.add(creator.user_id)));
      setResult(reply);
    } catch (runError) {
      setResult(null);
      setError(runError instanceof Error ? runError.message : 'Could not load recommendations. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-start justify-between gap-3 border-b border-background-200 px-5 py-3">
        <div>
          <h2 className="inline-flex items-center gap-1.5 font-heading text-sm font-semibold text-accent-800">
            <i className="ri-sparkling-2-line" />
            AI recommendations
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">At-risk creators and replacements from the Twiva pool.</p>
        </div>
        <Button size="sm" variant="outline" icon={busy ? 'ri-loader-4-line' : 'ri-search-eye-line'} disabled={busy} onClick={() => void run()}>
          {busy ? 'Analysing…' : result ? 'Run again' : 'Run analysis'}
        </Button>
      </header>
      <div className="space-y-4 px-5 py-4">
        {error ? (
          <p role="alert" className="flex items-start gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line mt-0.5" />
            {error}
          </p>
        ) : null}
        {!result && !error ? (
          <p className="text-xs text-foreground-500">
            Flags creators who haven't responded, submitted or resubmitted in time, and suggests who could replace them.
          </p>
        ) : null}
        {result ? (
          <>
            <p className="text-sm leading-relaxed text-foreground-700">{result.summary}</p>
            {result.at_risk.length > 0 ? (
              <div>
                <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">At risk</p>
                <ul className="mt-1.5 space-y-2">
                  {result.at_risk.map((creator) => (
                    <li key={creator.creator_id} className="text-xs">
                      <span className="font-medium text-foreground-900">{creator.name}</span>
                      <span className="text-foreground-500"> · {creator.status}</span>
                      <p className="text-foreground-600">{creator.reason}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.replacements.length > 0 ? (
              <div>
                <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">Possible replacements</p>
                <ul className="mt-1.5 space-y-1.5">
                  {result.replacements.map((creator) => (
                    <li key={creator.user_id} className="flex items-center justify-between gap-2 text-xs">
                      <Link to={`/creators/${creator.user_id}`} className="truncate font-medium text-secondary-800 hover:underline">
                        {creator.name || 'Unnamed creator'}
                      </Link>
                      <span className="shrink-0 text-foreground-500">
                        {creator.followers} on {creator.platforms}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.source !== 'ai' ? (
              <p className="text-[11px] text-foreground-400">Rule-based result: the AI summary wasn't available.</p>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
